'use client';

import { useEffect, useLayoutEffect, useRef, useState } from 'react';

// useLayoutEffect on the client, useEffect on the server (avoids SSR warning).
const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const STORAGE_KEY = 'kaympus:preloader-shown';

// Brand colors sampled from the Kaympus logo.
const NAVY = '#000E5E';
const BLUE = '#3D5EE1';

// Read the "already shown" flag. Only safe to call on the client — the
// server always gets `false`. We read it in a pre-paint layout effect so
// repeat visits never visibly render the overlay.
const hasSeenPreloader = () => {
    if (typeof window === 'undefined') return false;
    try {
        return window.sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch {
        // sessionStorage can throw in privacy modes — treat as "not seen"
        // so the preloader still plays.
        return false;
    }
};

const LETTERS = ['K', 'A', 'Y', 'M', 'P', 'U', 'S'];

/**
 * Preloader
 *
 * Full-screen overlay shown on the first paint of the marketing landing.
 * Pure CSS typography animation — the KAYMPUS wordmark staggers in with a
 * shimmer sweep (Sora, the landing's display font; no external Lottie or
 * network asset). Stays visible for `minDuration` — long enough for the
 * letter intro plus one shimmer pass — and then leaves.
 *
 * It deliberately does NOT wait for `window.load`. It used to, and on a
 * throttled mobile connection `load` did not fire until ~15s, because it waits
 * on every image on the page including the ones far below the fold. The
 * overlay is `position: fixed` over the whole viewport and holds a scroll
 * lock, so that turned the *slowest* asset on the page into the gate on the
 * visitor seeing or touching anything at all — a brand animation was making
 * the site feel broken on exactly the devices that could least afford it. The
 * content behind it is server-rendered and complete well before then; the only
 * thing `load` bought was that late images had finished decoding, which the
 * hero's own `priority` fetch already covers for the part that is visible.
 *
 * Shown once per browser tab session — `sessionStorage` is set when the
 * first run completes, so client-side navigations and in-tab reloads to the
 * same site don't re-trigger it. Closing the tab or opening a new window
 * clears the flag, which is the standard preloader behaviour.
 */
export default function Preloader({
    minDuration = 2200,
    fadeDuration = 350,
    onComplete,
}) {
    // IMPORTANT: initial state must be identical on server and client to
    // avoid a hydration mismatch. We start as "loading" on both, then
    // short-circuit in a layout effect (pre-paint) if sessionStorage says
    // we've already shown the preloader this session.
    const [isLoading, setIsLoading] = useState(true);
    const [isFading, setIsFading] = useState(false);
    const minElapsedRef = useRef(false);
    const finishedRef = useRef(false);

    // Once-per-session skip: runs BEFORE paint on the client, so repeat
    // visits never actually see the overlay. Runs as useEffect on the
    // server (which is a no-op during SSR) so the server still renders
    // the overlay, keeping server and client HTML identical for hydration.
    useIsoLayoutEffect(() => {
        if (hasSeenPreloader()) {
            setIsLoading(false);
        }
    }, []);

    // Lock scroll BEFORE paint while loading, and restore the moment
    // isLoading flips to false. Returning `null` from render does NOT
    // unmount the component, so we can't rely on the effect cleanup —
    // we have to gate on `isLoading`.
    useIsoLayoutEffect(() => {
        if (typeof document === 'undefined') return undefined;
        const { body, documentElement } = document;
        if (isLoading) {
            const prevBody = body.style.overflow;
            const prevHtml = documentElement.style.overflow;
            body.style.overflow = 'hidden';
            documentElement.style.overflow = 'hidden';
            return () => {
                body.style.overflow = prevBody;
                documentElement.style.overflow = prevHtml;
            };
        }
        // Not loading — make sure scroll is free.
        body.style.overflow = '';
        documentElement.style.overflow = '';
        return undefined;
    }, [isLoading]);

    useEffect(() => {
        // If we've already shown the preloader this session, skip all
        // timers/listeners entirely — the component already rendered null.
        if (!isLoading) return undefined;

        let hideTimeoutId;

        const tryFinish = () => {
            if (finishedRef.current) return;
            if (!minElapsedRef.current) return;
            finishedRef.current = true;
            // Mark as seen as soon as we commit to exiting, so a subsequent
            // mount (e.g. React Fast Refresh in dev) doesn't replay it.
            try {
                window.sessionStorage.setItem(STORAGE_KEY, '1');
            } catch {
                // Ignore — privacy mode etc.
            }
            setIsFading(true);
            hideTimeoutId = setTimeout(() => {
                setIsLoading(false);
                if (typeof onComplete === 'function') onComplete();
            }, fadeDuration);
        };

        // Minimum-display timer (letter intro + one shimmer pass) — and now
        // the only gate. See the note on the component for why `window.load`
        // is no longer one.
        const minTimeoutId = setTimeout(() => {
            minElapsedRef.current = true;
            tryFinish();
        }, minDuration);

        return () => {
            clearTimeout(minTimeoutId);
            clearTimeout(hideTimeoutId);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!isLoading) return null;

    return (
        <div
            aria-hidden={isFading}
            role="status"
            aria-label="Loading"
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 2147483647,
                background: '#FAFCFF',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 26,
                opacity: isFading ? 0 : 1,
                transition: `opacity ${fadeDuration}ms ease-in-out`,
                pointerEvents: isFading ? 'none' : 'auto',
            }}
        >
            <style>{`
                @keyframes klp-rise {
                    0%   { opacity: 0; transform: translateY(26px) scale(0.92); filter: blur(6px); }
                    60%  { opacity: 1; filter: blur(0); }
                    100% { opacity: 1; transform: translateY(0) scale(1); }
                }
                @keyframes klp-shimmer {
                    0%   { background-position: -200% center; }
                    100% { background-position: 200% center; }
                }
                @keyframes klp-bar {
                    0%   { left: -35%; width: 35%; }
                    50%  { left: 30%;  width: 40%; }
                    100% { left: 100%; width: 35%; }
                }
                @keyframes klp-dotpulse {
                    0%, 100% { transform: scale(1);   opacity: 0.9; }
                    50%      { transform: scale(1.5); opacity: 0.35; }
                }
                .klp-word {
                    display: flex;
                    align-items: baseline;
                    font-family: var(--font-landing, system-ui), system-ui, sans-serif;
                    font-weight: 800;
                    font-size: clamp(40px, 9vw, 76px);
                    letter-spacing: 0.06em;
                    line-height: 1;
                    user-select: none;
                }
                .klp-letter {
                    display: inline-block;
                    opacity: 0;
                    color: ${NAVY};
                    animation: klp-rise 640ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
                    background: linear-gradient(100deg, ${NAVY} 42%, ${BLUE} 50%, ${NAVY} 58%);
                    background-size: 200% auto;
                    -webkit-background-clip: text;
                    background-clip: text;
                    -webkit-text-fill-color: transparent;
                    animation: klp-rise 640ms cubic-bezier(0.22, 1, 0.36, 1) forwards,
                               klp-shimmer 2.4s ease-in-out 900ms infinite;
                }
                .klp-letter.klp-k {
                    background: linear-gradient(100deg, ${BLUE} 42%, ${NAVY} 50%, ${BLUE} 58%);
                    background-size: 200% auto;
                    -webkit-background-clip: text;
                    background-clip: text;
                    -webkit-text-fill-color: transparent;
                }
                .klp-dot {
                    width: 9px; height: 9px;
                    margin-left: 10px;
                    border-radius: 50%;
                    background: ${BLUE};
                    align-self: flex-end;
                    margin-bottom: 6px;
                    opacity: 0;
                    animation: klp-rise 640ms cubic-bezier(0.22, 1, 0.36, 1) 620ms forwards,
                               klp-dotpulse 1.6s ease-in-out 1300ms infinite;
                }
                .klp-tag {
                    font-family: var(--font-landing, system-ui), system-ui, sans-serif;
                    font-size: 12px;
                    font-weight: 600;
                    letter-spacing: 0.42em;
                    text-transform: uppercase;
                    color: ${NAVY};
                    opacity: 0;
                    animation: klp-rise 700ms ease 750ms forwards;
                }
                .klp-tag span { color: ${BLUE}; }
                .klp-track {
                    position: relative;
                    width: min(220px, 46vw);
                    height: 3px;
                    border-radius: 999px;
                    background: rgba(0, 14, 94, 0.10);
                    overflow: hidden;
                    opacity: 0;
                    animation: klp-rise 600ms ease 950ms forwards;
                }
                .klp-track::after {
                    content: '';
                    position: absolute;
                    top: 0; bottom: 0;
                    border-radius: 999px;
                    background: linear-gradient(90deg, ${BLUE}, ${NAVY});
                    animation: klp-bar 1.4s ease-in-out 1100ms infinite;
                }
                @media (prefers-reduced-motion: reduce) {
                    .klp-letter, .klp-dot, .klp-tag, .klp-track { animation-duration: 1ms !important; animation-delay: 0ms !important; }
                    .klp-track::after { animation: none; left: 0; width: 100%; }
                }
            `}</style>

            <div className="klp-word" data-testid="landing-preloader-wordmark">
                {LETTERS.map((ch, i) => (
                    <span
                        key={i}
                        className={`klp-letter${ch === 'K' && i === 0 ? ' klp-k' : ''}`}
                        style={{ animationDelay: `${i * 70}ms, ${900 + i * 40}ms` }}
                    >
                        {ch}
                    </span>
                ))}
                <span className="klp-dot" aria-hidden="true" />
            </div>

            <div className="klp-tag">
                Powered by <span>Kayease</span>
            </div>

            <div className="klp-track" aria-hidden="true" />
        </div>
    );
}

